import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerAccountsAdminApi, PartnerAccountAdminView } from '../../api/admin/partnerAccounts';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected', suspended: 'Suspended' };

/**
 * Real admin review for external partner-organization registrations.
 * Only accounts that have verified their email even appear in the
 * pending queue - an unverified/fake signup is never something an
 * admin has to sift through.
 */
export function PartnerAccountsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const allQuery = useQuery({ queryKey: ['partner-accounts-all'], queryFn: () => partnerAccountsAdminApi.listAll() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-accounts-all'] });

  const approveMutation = useMutation({ mutationFn: (id: number) => partnerAccountsAdminApi.approve(id), onSuccess: () => { showSuccess('Partner account approved.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const rejectMutation = useMutation({ mutationFn: ({ id, reason }: { id: number; reason: string }) => partnerAccountsAdminApi.reject(id, reason), onSuccess: () => { showSuccess('Partner account rejected.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const suspendMutation = useMutation({ mutationFn: (id: number) => partnerAccountsAdminApi.suspend(id), onSuccess: () => { showSuccess('Partner account suspended.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="page">
      <div className="page-header"><h1>Partner Accounts</h1></div>
      <p className="muted">External partner-organization registrations. Approved accounts can sign in but currently have no access to VConnect data or modules.</p>

      <section className="card">
        {allQuery.data && (
          <DataTable<PartnerAccountAdminView>
            rows={allQuery.data.data}
            rowKey={(a) => a.id}
            emptyMessage="No partner account registrations yet."
            columns={[
              { header: 'Name', render: (a) => a.full_name },
              { header: 'Organization', render: (a) => a.organization_name },
              { header: 'Email', render: (a) => a.email },
              { header: 'Email Verified', render: (a) => a.email_verified_at ? 'Yes' : 'No' },
              { header: 'Status', render: (a) => <StatusBadge status={a.status} label={STATUS_LABELS[a.status]} /> },
              { header: 'Registered', render: (a) => a.created_at },
              {
                header: '',
                render: (a) => (
                  <span className="row-actions">
                    {a.status === 'pending_approval' && a.email_verified_at && (
                      <>
                        <button className="link-button" onClick={() => approveMutation.mutate(a.id)}>Approve</button>
                        <button className="link-button" onClick={() => { const reason = window.prompt('Reason for rejection:'); if (reason) rejectMutation.mutate({ id: a.id, reason }); }}>Reject</button>
                      </>
                    )}
                    {a.status === 'approved' && <button className="link-button" onClick={() => suspendMutation.mutate(a.id)}>Suspend</button>}
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
}
